import { all, call, put, select, takeLatest } from '@redux-saga/core/effects';
import {
  INTEGRATION_LEVEL_TYPE,
  INTEGRATION_VENDORS,
} from '../../../common/constants/IntegrationConstants';
import { request } from '../../../common/util/RestClient';
import envConfig from '../../../config';
import {
  fetchIntegrations,
  fetchIntegretionMetaData,
} from '../../routines/integrations/integrationDataRoutines';
import {
  connectIntegration,
  disconnectIntegration,
  udpateIntegreation,
} from '../../routines/integrations/integrationSetupRoutines';
import { selectLoggedInUser } from '../../selectors/userSelector';
import { showWarningMessage } from '../../routines/notificationRoutines';
import { NO_OUTLOOK_CONFIG_EMAIL_WARNING } from '../../../common/constants/EmailConstants';

function* fetchIntegrationMetadataSaga(action) {
  const { type, vendor } = action.payload;
  try {
    const url = `${envConfig.APIG_URL}integration/types/${type}/vendors/${vendor}/integration-metadata`;
    const { data: metadata } = yield call(request, url);
    yield put(
      fetchIntegretionMetaData.success({ [type]: { [vendor]: metadata } })
    );
  } catch (error) {
    const isNotFound = error.response.data?.message === 'Not Found';
    if (isNotFound && vendor === INTEGRATION_VENDORS.OUTLOOK) {
      yield put(
        showWarningMessage.success({
          warningMessage: true,
          message: NO_OUTLOOK_CONFIG_EMAIL_WARNING,
        })
      );
      yield put(fetchIntegretionMetaData.success({}));
    } else {
      yield put(fetchIntegretionMetaData.failure({ error }));
    }
  }
}

function* connectIntegrationSaga(action) {
  try {
    const { type, vendor, integrationContext, integrationType } =
      action.payload;
    let partialUrl = '';
    if (integrationType === 'user') {
      const { userId } = yield select(selectLoggedInUser);
      partialUrl = `users/${userId}/`;
    }
    const url = `${envConfig.APIG_URL}integration/${partialUrl}service-integrations`;

    const config = {
      method: 'POST',
      payload: {
        type,
        vendor,
        integrationContext,
      },
    };

    const { data: integration } = yield call(request, url, config);

    yield put(
      connectIntegration.success({
        integrationType,
        type,
        vendor,
        integration,
        successMessage: 'Integration connected successfully',
      })
    );

    yield put(fetchIntegrations());
  } catch (error) {
    yield put(connectIntegration.failure({ error }));
  }
}

function* reconnectIntegrationSaga(action) {
  try {
    const { integrationId, integrationType, type, vendor, integrationContext } =
      action.payload;
    const isUserIntegration = integrationType === INTEGRATION_LEVEL_TYPE.USER;

    let url;
    if (isUserIntegration) {
      const { userId } = yield select(selectLoggedInUser);
      url = `${envConfig.APIG_URL}integration/users/${userId}/service-integrations/${integrationId}`;
    } else {
      url = `${envConfig.APIG_URL}integration/service-integrations`;
    }

    const config = {
      method: 'PUT',
      payload: {
        type,
        vendor,
        integrationContext,
      },
    };

    const { data: integration } = yield call(request, url, config);

    yield put(
      connectIntegration.success({
        integration,
        successMessage: 'Integration reconnected successfully',
      })
    );

    yield put(fetchIntegrations());
  } catch (error) {
    yield put(connectIntegration.failure({ error }));
  }
}

function* disconnectSaga(action) {
  try {
    const { integrationId, integrationType, userId } = action.payload;

    let url;
    const isUserIntegration = integrationType === INTEGRATION_LEVEL_TYPE.USER;
    if (isUserIntegration) {
      url = `${envConfig.APIG_URL}integration/users/${userId}/service-integrations/${integrationId}`;
    } else {
      url = `${envConfig.APIG_URL}integration/service-integrations/${integrationId}`;
    }

    const config = {
      method: 'DELETE',
    };

    yield call(request, url, config);

    yield put(
      disconnectIntegration.success({
        successMessage: 'Integration disconnected successfully',
      })
    );

    yield put(fetchIntegrations());
  } catch (error) {
    yield put(disconnectIntegration.failure({ error }));
  } finally {
    yield put(disconnectIntegration.fulfill());
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchIntegretionMetaData.TRIGGER, fetchIntegrationMetadataSaga),
    takeLatest(connectIntegration.TRIGGER, connectIntegrationSaga),
    takeLatest(udpateIntegreation.TRIGGER, reconnectIntegrationSaga),
    takeLatest(disconnectIntegration.TRIGGER, disconnectSaga),
  ]);
}
